import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LangProvider } from './context/LangContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';

// Pages
import LoginPage from './pages/auth/LoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminModulesPage from './pages/admin/AdminModulesPage';
import AdminModuleDetailPage from './pages/admin/AdminModuleDetailPage';
import AdminStudentsPage from './pages/admin/AdminStudentsPage';
import AdminReportsPage from './pages/admin/AdminReportsPage';
import StudentModulesPage from './pages/student/StudentModulesPage';
import StudentModulePlayerPage from './pages/student/StudentModulePlayerPage';

// Layout
import Layout from './components/shared/Layout';

// Protected route wrapper
function ProtectedRoute({ children, requireAdmin = false, allowSuperadminAsStudent = false }) {
  const { user, loading, effectiveRole } = useAuth();

  console.log('🛡️ ProtectedRoute:', { 
    user: user?.email, 
    loading, 
    effectiveRole, 
    requireAdmin, 
    allowSuperadminAsStudent 
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <svg className="w-7 h-7 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
      </div>
    );
  }

  if (!user) {
    console.log('❌ Sem usuário, redirecionando para login');
    return <Navigate to="/login" replace />;
  }
  
  // Se requer admin e não é admin/superadmin, redireciona para student
  if (requireAdmin && effectiveRole !== 'admin' && effectiveRole !== 'superadmin') {
    console.log('❌ Requer admin mas usuário não é admin, redirecionando para student');
    return <Navigate to="/student" replace />;
  }
  
  // Se é rota de student e é admin (mas não superadmin), redireciona para admin
  // Superadmin pode acessar área de student (allowSuperadminAsStudent)
  if (!requireAdmin && effectiveRole === 'admin') {
    console.log('❌ Admin tentando acessar área de student, redirecionando para admin');
    return <Navigate to="/admin" replace />;
  }

  console.log('✅ Acesso permitido');
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user, effectiveRole } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={
        user ? <Navigate to={effectiveRole === 'admin' || effectiveRole === 'superadmin' ? '/admin' : '/student'} replace /> : <LoginPage />
      } />

      {/* Admin routes */}
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin>
          <AdminDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/dashboard" element={
        <ProtectedRoute requireAdmin>
          <AdminDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/modules" element={
        <ProtectedRoute requireAdmin>
          <AdminModulesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/modules/:id" element={
        <ProtectedRoute requireAdmin>
          <AdminModuleDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/students" element={
        <ProtectedRoute requireAdmin>
          <AdminStudentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute requireAdmin>
          <AdminReportsPage />
        </ProtectedRoute>
      } />

      {/* Student routes - Superadmin pode acessar */}
      <Route path="/student" element={
        <ProtectedRoute allowSuperadminAsStudent>
          <StudentModulesPage />
        </ProtectedRoute>
      } />
      <Route path="/student/modules" element={
        <ProtectedRoute allowSuperadminAsStudent>
          <StudentModulesPage />
        </ProtectedRoute>
      } />
      <Route path="/student/module/:moduleId" element={
        <ProtectedRoute allowSuperadminAsStudent>
          <StudentModulePlayerPage />
        </ProtectedRoute>
      } />
      <Route path="/student/module/:moduleId/video/:videoId" element={
        <ProtectedRoute allowSuperadminAsStudent>
          <StudentModulePlayerPage />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <LangProvider>
            <ToastProvider>
              <AppRoutes />
            </ToastProvider>
          </LangProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
